import React, { useState, useEffect } from 'react';
import { useTranslate } from 'react-admin';
import { useNavigate } from 'react-router-dom';
import { peerReviewService, PeerReviewAssignment } from '../../services/peerReviewService';
import MaterialSymbol from '../common/MaterialSymbol';

interface PendingReviewsWidgetProps {
  limit?: number;
}

/**
 * "Ausstehende Reviews" — Panel im neuen Design (.revrow).
 * Gleicher Datenabruf (peerReviewService.getPendingReviews) und gleiche
 * Navigation (/peer-review, /peer-review/:id) wie zuvor.
 */
const PendingReviewsWidget: React.FC<PendingReviewsWidgetProps> = ({ limit = 3 }) => {
  const [reviews, setReviews] = useState<PeerReviewAssignment[]>([]);
  const [loading, setLoading] = useState(true);
  const translate = useTranslate();
  const navigate = useNavigate();

  useEffect(() => {
    loadPendingReviews();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadPendingReviews = async () => {
    try {
      const data = await peerReviewService.getPendingReviews();
      setReviews(data.slice(0, limit));
    } catch (error) {
      console.error('Error loading pending reviews:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="panel">
        <div className="skel" />
        <div className="skel" />
      </div>
    );
  }

  return (
    <div className="panel">
      <div className="sec-head">
        <h2 className="sec-title">
          <span className="ico"><MaterialSymbol name="rate_review" size={19} /></span>
          {translate('dashboard.pendingReviews', 'Ausstehende Reviews')}
          {reviews.length > 0 && <span className="count-badge">{reviews.length}</span>}
        </h2>
        <button type="button" className="seeall" onClick={() => navigate('/peer-review')}>
          {translate('common.viewAll', 'Alle anzeigen')}
          <MaterialSymbol name="arrow_forward" size={15} />
        </button>
      </div>

      {reviews.length === 0 ? (
        <p className="empty-note">
          {translate('peerReview.noPendingReviews', 'Keine ausstehenden Reviews')}
        </p>
      ) : (
        reviews.map((review) => (
          <div className="revrow" key={review.id}>
            <div className="body">
              <div className="ttl">{review.lessonTitle}</div>
              <div className="sub">
                {translate('peerReview.assignedOn', {
                  _: 'Zugewiesen am %{date}',
                  date: new Date(review.assignedAt).toLocaleDateString('de-DE'),
                })}
              </div>
            </div>
            <button
              type="button"
              className="btn-primary"
              onClick={() => navigate(`/peer-review/${review.id}`)}
            >
              {translate('peerReview.review', 'Bewerten')}
            </button>
          </div>
        ))
      )}
    </div>
  );
};

export default PendingReviewsWidget;
